import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/pages/Dashboard";
import { api, formatApiError } from "@/lib/api";
import { RouteForm } from "@/components/RouteForm";
import { CARGO_LABELS, CARGO_LIST, VEHICLE_TYPES, ROLE_LABELS, STATUS_LABELS, formatEUR, formatDate, formatCo2 } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Truck, Route as RouteIcon, Inbox, Newspaper, BadgeCheck, RefreshCw, Pencil, Trash2, Plus, MapPin, ArrowRight, Leaf } from "lucide-react";

const statusColor = { pending: "bg-amber-500", accepted: "bg-emerald-600", rejected: "bg-destructive", completed: "bg-primary", active: "bg-emerald-600", cancelled: "bg-muted-foreground" };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  const loadRoutes = () => api.get("/admin/routes").then(({ data }) => setRoutes(data)).catch(() => {});
  const loadBookings = () => api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  const loadAll = () => { loadStats(); loadUsers(); loadRoutes(); loadBookings(); };
  useEffect(() => { loadAll(); }, []);

  const cards = stats ? [
    { label: "Utenti totali", value: stats.users, icon: Users },
    { label: "Trasportatori", value: stats.carriers, icon: Truck },
    { label: "Tratte attive", value: stats.active_routes, icon: RouteIcon },
    { label: "CO₂ risparmiata", value: formatCo2(routes.reduce((s, r) => s + (r.co2_saved_kg || 0), 0)), icon: Leaf, eco: true },
    { label: "Privati", value: stats.privates, icon: Users },
    { label: "Prenotazioni", value: stats.bookings, icon: Inbox },
    { label: "In attesa", value: stats.pending_bookings, icon: Inbox },
    { label: "Articoli news", value: stats.news, icon: Newspaper },
  ] : [];

  const delRoute = async (id) => {
    try { await api.delete(`/routes/${id}`); toast.success("Tratta eliminata"); loadRoutes(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setBookingStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status`, { status }); toast.success("Aggiornato"); loadBookings(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell title="Amministrazione" subtitle="Controllo completo: utenti, tratte e prenotazioni."
      actions={
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="admin-add-route-btn"><Plus size={16} /> Nuova tratta</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
            <DialogHeader><DialogTitle className="font-display">Crea tratta</DialogTitle></DialogHeader>
            <RouteForm onCreated={() => { setOpenCreate(false); loadRoutes(); loadStats(); }} />
          </DialogContent>
        </Dialog>
      }>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className={`border rounded-sm p-4 ${c.eco ? "border-emerald-200 bg-emerald-50" : "border-border bg-card"}`} data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-muted-foreground">{c.label}</span>
              <c.icon size={16} className={c.eco ? "text-emerald-600" : "text-primary"} />
            </div>
            <p className={`font-display font-extrabold text-3xl mt-2 ${c.eco ? "text-emerald-700" : ""}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="users">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="rounded-sm">
            <TabsTrigger value="users" data-testid="admin-tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="routes" data-testid="admin-tab-routes">Tratte</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Prenotazioni</TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={loadAll} data-testid="admin-refresh-btn"><RefreshCw size={14} /> Aggiorna</Button>
        </div>

        {/* USERS */}
        <TabsContent value="users">
          <div className="border border-border rounded-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead>
                <TableHead>Iscritto</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.name}{u.company ? <span className="block text-xs text-muted-foreground">{u.company}</span> : null}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{ROLE_LABELS[u.role]}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                    <TableCell>{u.verified ? <span className="flex items-center gap-1 text-emerald-600 text-sm"><BadgeCheck size={15} /> Verificato</span> : <span className="text-sm text-amber-600">In attesa</span>}</TableCell>
                    <TableCell className="text-right"><UserEditDialog user={u} onSaved={() => { loadUsers(); loadStats(); }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ROUTES */}
        <TabsContent value="routes" className="space-y-3">
          {routes.length === 0 && <p className="text-sm text-muted-foreground">Nessuna tratta.</p>}
          {routes.map((r) => (
            <div key={r.route_id} className="border border-border rounded-sm p-4 bg-card flex items-center justify-between gap-4" data-testid={`admin-route-${r.route_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-display font-semibold">
                  <MapPin size={15} className="text-primary" /> {r.origin} <ArrowRight size={14} className="text-accent" /> {r.destination}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.carrier_name} · {formatDate(r.departure_date)} · {r.cargo_types?.map((c) => CARGO_LABELS[c]).join(", ")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-display font-bold text-primary text-sm">{formatEUR(r.price_min)}</span>
                <Badge className={`rounded-sm text-white ${statusColor[r.status]}`}>{STATUS_LABELS[r.status]}</Badge>
                <RouteEditDialog route={r} onSaved={loadRoutes} />
                <Button variant="ghost" size="icon" className="rounded-sm text-destructive hover:bg-destructive/10" onClick={() => delRoute(r.route_id)} data-testid={`admin-delete-route-${r.route_id}`}><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* BOOKINGS */}
        <TabsContent value="bookings" className="space-y-3">
          {bookings.length === 0 && <p className="text-sm text-muted-foreground">Nessuna prenotazione.</p>}
          {bookings.map((b) => (
            <div key={b.booking_id} className="border border-border rounded-sm p-4 bg-card" data-testid={`admin-booking-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-semibold">{b.route_summary}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Da {b.user_name} · {CARGO_LABELS[b.cargo_type] || b.cargo_type}{b.weight_kg ? ` · ${b.weight_kg} kg` : ""}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm">
                    {b.offered_price ? <span>Offerta: <strong>{formatEUR(b.offered_price)}</strong></span> : null}
                    {b.insurance_enabled && <span className="text-primary">Polizza {formatEUR(b.insurance_premium)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={b.status} onValueChange={(v) => setBookingStatus(b.booking_id, v)}>
                    <SelectTrigger className="rounded-sm w-36 h-8 text-xs" data-testid={`admin-booking-status-${b.booking_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["pending", "accepted", "rejected", "completed"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}

function UserEditDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [company, setCompany] = useState(user.company || "");
  const [role, setRole] = useState(user.role);
  const [verified, setVerified] = useState(!!user.verified);

  const save = async () => {
    try {
      await api.put(`/admin/users/${user.user_id}`, { name, phone, company, role, verified });
      toast.success("Utente aggiornato"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid={`edit-user-${user.user_id}`}><Pencil size={13} /> Modifica</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader><DialogTitle className="font-display">Modifica utente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1" data-testid="edit-user-name" /></div>
          <div><Label className="text-xs">Telefono</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-sm mt-1" data-testid="edit-user-phone" /></div>
          <div><Label className="text-xs">Ragione sociale</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-sm mt-1" data-testid="edit-user-company" /></div>
          <div>
            <Label className="text-xs">Ruolo</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Privato</SelectItem>
                <SelectItem value="carrier">Trasportatore</SelectItem>
                <SelectItem value="admin">Amministratore</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between border border-border rounded-sm p-3">
            <span className="text-sm">Verificato</span>
            <Switch checked={verified} onCheckedChange={setVerified} data-testid="edit-user-verified" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full" data-testid="save-user-btn">Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RouteEditDialog({ route, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    origin: route.origin, destination: route.destination,
    stopsText: (route.stops || []).join(", "),
    cargo_types: route.cargo_types || [],
    departure_date: route.departure_date || "", arrival_date: route.arrival_date || "",
    price_min: route.price_min ?? "", price_max: route.price_max ?? "",
    partial_available: !!route.partial_available, vehicle_type: route.vehicle_type || "",
    distance_km: route.distance_km ?? "",
    capacity_note: route.capacity_note || "", notes: route.notes || "", status: route.status || "active",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCargo = (c) => setF((p) => ({ ...p, cargo_types: p.cargo_types.includes(c) ? p.cargo_types.filter((x) => x !== c) : [...p.cargo_types, c] }));

  const save = async () => {
    try {
      await api.put(`/routes/${route.route_id}`, {
        origin: f.origin, destination: f.destination,
        stops: f.stopsText.split(",").map((s) => s.trim()).filter(Boolean),
        cargo_types: f.cargo_types,
        departure_date: f.departure_date, arrival_date: f.arrival_date || null,
        price_min: Number(f.price_min || 0), price_max: Number(f.price_max || 0),
        partial_available: f.partial_available, vehicle_type: f.vehicle_type || null,
        distance_km: Number(f.distance_km || 0),
        capacity_note: f.capacity_note || null, notes: f.notes || null, status: f.status,
      });
      toast.success("Tratta aggiornata"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-sm" data-testid={`edit-route-${route.route_id}`}><Pencil size={15} /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="font-display">Modifica tratta</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Origine</Label><Input value={f.origin} onChange={(e) => set("origin", e.target.value)} className="rounded-sm mt-1" data-testid="edit-route-origin" /></div>
            <div><Label className="text-xs">Destinazione</Label><Input value={f.destination} onChange={(e) => set("destination", e.target.value)} className="rounded-sm mt-1" data-testid="edit-route-destination" /></div>
          </div>
          <div><Label className="text-xs">Tappe (virgola)</Label><Input value={f.stopsText} onChange={(e) => set("stopsText", e.target.value)} className="rounded-sm mt-1" data-testid="edit-route-stops" /></div>
          <div>
            <Label className="text-xs mb-2 block">Tipologie merce</Label>
            <div className="flex flex-wrap gap-2">
              {CARGO_LIST.map((c) => (
                <button key={c} type="button" onClick={() => toggleCargo(c)}
                  className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${f.cargo_types.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}>{CARGO_LABELS[c]}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Data partenza</Label><Input type="date" value={f.departure_date} onChange={(e) => set("departure_date", e.target.value)} className="rounded-sm mt-1" data-testid="edit-route-departure" /></div>
            <div><Label className="text-xs">Data arrivo</Label><Input type="date" value={f.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} className="rounded-sm mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Prezzo min (€)</Label><Input type="number" value={f.price_min} onChange={(e) => set("price_min", e.target.value)} className="rounded-sm mt-1" data-testid="edit-route-pricemin" /></div>
            <div><Label className="text-xs">Prezzo max (€)</Label><Input type="number" value={f.price_max} onChange={(e) => set("price_max", e.target.value)} className="rounded-sm mt-1" /></div>
            <div>
              <Label className="text-xs">Mezzo</Label>
              <Select value={f.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="edit-route-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Attiva</SelectItem>
                  <SelectItem value="completed">Completata</SelectItem>
                  <SelectItem value="cancelled">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3 mt-5">
              <span className="text-sm">Trasporti parziali</span>
              <Switch checked={f.partial_available} onCheckedChange={(v) => set("partial_available", v)} />
            </div>
          </div>
          <div><Label className="text-xs">Capacità</Label><Input value={f.capacity_note} onChange={(e) => set("capacity_note", e.target.value)} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs">Distanza tratta (km)</Label><Input type="number" value={f.distance_km} onChange={(e) => set("distance_km", e.target.value)} placeholder="es. 770" className="rounded-sm mt-1" data-testid="edit-route-distance" /></div>
          <div><Label className="text-xs">Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm mt-1" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full" data-testid="save-route-btn">Salva modifiche</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
